import { useCallback, useEffect, useState } from 'react'
import { connectionsApi, guardianApi, profileApi } from '../api/endpoints'
import { isAbortError } from '../api/client'
import type { ConnectionRequestResponse, ConnectionStatus } from '../types/api'

/**
 * Whether the signed-in participant may press Connect, and on whom.
 *
 * <p>Two separate questions that both end at the same button, which is why they are answered in
 * one place rather than re-derived on each map:
 *
 * 1. **Is this account allowed to connect at all?** A minor whose parent has not been linked and
 *    confirmed cannot start or receive any connection, so every Connect button is dead until then.
 * 2. **Is there already a connection with this person?** Once one exists - accepted, pending, or
 *    sitting with a parent - the button must not come back, or the same pair collects duplicate
 *    rows and "are these two connected" stops having one answer.
 *
 * The server enforces both regardless (see ConnectionService.create); this only decides what the
 * UI shows, so a stale page cannot do damage, it can only look wrong until it reloads.
 */
export interface ConnectionGate {
  loading: boolean
  /** True when this account is a minor with no confirmed parent - all Connect buttons off. */
  blockedPendingGuardian: boolean
  /** Why, in words the participant can act on. Null when not blocked. */
  blockedReason: string | null
  /** The live status with one participant, or null when there has never been a request. */
  statusWith: (participantId: string) => ConnectionStatus | null
  /** False when blocked, or when a connection already exists or is being decided. */
  canConnectWith: (participantId: string) => boolean
  /** A short label for an existing connection, for where the button used to be. */
  labelFor: (participantId: string) => string | null
  refresh: () => void
}

/** Statuses that mean "do not offer Connect again". A closed request may be retried. */
const LIVE: ConnectionStatus[] = ['AWAITING_GUARDIAN', 'PENDING', 'ACCEPTED']

const LABELS: Partial<Record<ConnectionStatus, string>> = {
  ACCEPTED: 'Connected',
  PENDING: 'Request sent',
  AWAITING_GUARDIAN: 'Waiting for parent approval',
}

export function useConnectionGate(): ConnectionGate {
  const [loading, setLoading] = useState(true)
  const [blockedPendingGuardian, setBlocked] = useState(false)
  const [connections, setConnections] = useState<ConnectionRequestResponse[]>([])
  const [selfId, setSelfId] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    async function load() {
      setLoading(true)
      try {
        const [profile, conns] = await Promise.all([
          profileApi.getOwn(),
          connectionsApi.listOwn(),
        ])
        if (cancelled) return
        setSelfId(profile.id)
        setConnections(conns)

        // Only a minor is gated, so only a minor pays for the extra call.
        if (profile.isMinor) {
          const links = await guardianApi.listMyLinks()
          if (cancelled) return
          // ACTIVE only. A PENDING link means the parent has been asked and has not answered -
          // nobody is supervising yet, so it must not unlock anything.
          const hasParent = links.some(
            (l) => l.childParticipantId === profile.id && l.status === 'ACTIVE',
          )
          setBlocked(!hasParent)
        } else {
          setBlocked(false)
        }
      } catch (err) {
        if (isAbortError(err) || cancelled) return
        // Fail open on the gate but closed on the buttons: if we cannot tell, we leave Connect
        // enabled and let the server refuse. Guessing "blocked" would lock out every adult the
        // moment this call fails.
        console.error('[connection-gate] could not load gating state', err)
        setBlocked(false)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [tick])

  const statusWith = useCallback(
    (participantId: string): ConnectionStatus | null => {
      if (!selfId) return null
      // Direction-agnostic, matching the server: a connection is a fact about the pair.
      const match = connections.find(
        (c) =>
          (c.senderParticipantId === selfId && c.receiverParticipantId === participantId) ||
          (c.receiverParticipantId === selfId && c.senderParticipantId === participantId),
      )
      return match?.status ?? null
    },
    [connections, selfId],
  )

  const canConnectWith = useCallback(
    (participantId: string) => {
      if (blockedPendingGuardian) return false
      const status = statusWith(participantId)
      return status === null || !LIVE.includes(status)
    },
    [blockedPendingGuardian, statusWith],
  )

  const labelFor = useCallback(
    (participantId: string) => {
      const status = statusWith(participantId)
      return status ? LABELS[status] ?? null : null
    },
    [statusWith],
  )

  return {
    loading,
    blockedPendingGuardian,
    blockedReason: blockedPendingGuardian
      ? 'A parent or guardian has to be linked and confirm before you can connect with anyone.'
      : null,
    statusWith,
    canConnectWith,
    labelFor,
    refresh,
  }
}
